<template>
	<div class="moments-page">
		<div class="page-header">
			<div>
				<h2>动态</h2>
				<p>记录日常，轻量互动</p>
			</div>
			<div class="date-filter" v-if="selectedDate">
				<span>当前筛选：{{ selectedDate }}</span>
				<button @click="clearDateFilter">清除</button>
			</div>
		</div>

		<div class="moments-layout">
			<section class="moments-main">
				<MomentFeedCard
					v-for="moment in momentList"
					:key="moment.id"
					:moment="moment"
					:liked="isLike(moment.id)"
					:comment-open="expandedMomentId === moment.id"
					:comment-count="commentCountMap[moment.id] || 0"
					:default-author-name="$store.state.introduction.name"
					:default-author-avatar="$store.state.introduction.avatar"
					@like="like"
					@toggle-comment="toggleComment">
					<MomentCommentPanel
						:moment-id="moment.id"
						:visible="expandedMomentId === moment.id"
						@summary-change="updateCommentSummary" />
				</MomentFeedCard>

				<el-empty v-if="momentList.length === 0" description="当天暂无动态" />

				<el-pagination
					@current-change="handleCurrentChange"
					:current-page="pageNum"
					:page-count="totalPage"
					layout="prev, pager, next"
					background
					hide-on-single-page
					class="pagination" />
			</section>

			<MomentTimeline
				:items="timeline"
				:selected-date="selectedDate"
				:active-date="activeTimelineDate"
				@select="selectDate" />
		</div>
	</div>
</template>

<script>
import {getMomentListByPageNum, getMomentTimeline, likeMoment} from '@/api/moment'
import {getCommentListByQuery} from '@/api/comment'
import MomentFeedCard from '@/components/moments/MomentFeedCard.vue'
import MomentCommentPanel from '@/components/moments/MomentCommentPanel.vue'
import MomentTimeline from '@/components/moments/MomentTimeline.vue'

export default {
	name: 'Moments',
	components: {
		MomentFeedCard,
		MomentCommentPanel,
		MomentTimeline
	},
	data() {
		return {
			likeMomentIds: JSON.parse(window.localStorage.getItem('likeMomentIds') || '[]'),
			momentList: [],
			pageNum: 1,
			totalPage: 0,
			timeline: [],
			selectedDate: '',
			activeTimelineDate: '',
			expandedMomentId: null,
			commentCountMap: {},
			momentObserver: null
		}
	},
	created() {
		this.selectedDate = this.$route.query.date || ''
		this.getMomentTimeline()
		this.getMomentList()
	},
	beforeDestroy() {
		this.destroyMomentObserver()
	},
	watch: {
		likeMomentIds(newValue) {
			window.localStorage.setItem('likeMomentIds', JSON.stringify(newValue))
		},
		'$route.query.date'(newDate) {
			const normalized = newDate || ''
			if (normalized === this.selectedDate) {
				return
			}
			this.selectedDate = normalized
			this.pageNum = 1
			this.getMomentList()
		}
	},
	methods: {
		isLike(id) {
			return this.likeMomentIds.indexOf(id) > -1
		},
		getMomentList(scrollToDate = false) {
			const adminToken = window.localStorage.getItem('adminToken')
			const token = adminToken || ''
			getMomentListByPageNum(token, this.pageNum, this.selectedDate).then(res => {
				if (res.code !== 200) {
					this.msgError(res.msg)
					return
				}
				this.momentList = res.data.list || []
				this.totalPage = res.data.totalPage || 0
				this.expandedMomentId = null
				if (this.selectedDate) {
					this.activeTimelineDate = this.selectedDate
				}
				this.$nextTick(() => {
					this.loadCommentSummaries()
					this.registerMomentObserver()
					if (scrollToDate && this.selectedDate) {
						this.scrollToDate(this.selectedDate)
					}
				})
			}).catch(() => {
				this.msgError('请求失败')
			})
		},
		getMomentTimeline() {
			const adminToken = window.localStorage.getItem('adminToken')
			const token = adminToken || ''
			getMomentTimeline(token).then(res => {
				if (res.code === 200) {
					this.timeline = res.data || []
				}
			})
		},
		loadCommentSummaries() {
			const token = window.localStorage.getItem('adminToken') || ''
			const requests = this.momentList.map(moment => {
				const query = {page: 3, momentId: moment.id, pageNum: 1, pageSize: 1}
				return getCommentListByQuery(token, query).then(res => {
					this.$set(this.commentCountMap, moment.id, Number(res.data.allComment || 0))
				}).catch(() => {
					this.$set(this.commentCountMap, moment.id, 0)
				})
			})
			Promise.all(requests).catch(() => {})
		},
		updateCommentSummary(payload) {
			if (!payload || !payload.momentId) {
				return
			}
			this.$set(this.commentCountMap, payload.momentId, Number(payload.count || 0))
		},
		selectDate(date) {
			if (!date) {
				this.clearDateFilter()
				return
			}
			this.selectedDate = date
			this.pageNum = 1
			this.$router.replace({path: '/moments', query: {date}})
			this.getMomentList(true)
		},
		clearDateFilter() {
			this.selectedDate = ''
			this.pageNum = 1
			this.activeTimelineDate = ''
			this.$router.replace({path: '/moments', query: {}})
			this.getMomentList()
		},
		scrollToDate(date) {
			const element = document.querySelector(`.moment-card[data-date="${date}"]`)
			if (!element) {
				return
			}
			const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 70
			window.scrollTo({top: offsetTop, behavior: 'smooth'})
		},
		handleCurrentChange(newPage) {
			this.pageNum = newPage
			this.getMomentList()
			this.scrollToTop()
		},
		toggleComment(momentId) {
			this.expandedMomentId = this.expandedMomentId === momentId ? null : momentId
		},
		registerMomentObserver() {
			this.destroyMomentObserver()
			if (this.selectedDate || this.momentList.length === 0) {
				this.activeTimelineDate = this.selectedDate
				return
			}
			const options = {
				root: null,
				threshold: [0.2, 0.5, 0.8],
			}
			this.momentObserver = new IntersectionObserver(entries => {
				const visible = entries
					.filter(item => item.isIntersecting)
					.sort((a, b) => b.intersectionRatio - a.intersectionRatio)
				if (visible.length === 0) {
					return
				}
				const date = visible[0].target.getAttribute('data-date') || ''
				if (date) {
					this.activeTimelineDate = date
				}
			}, options)
			document.querySelectorAll('.moment-card[data-date]').forEach(element => {
				this.momentObserver.observe(element)
			})
		},
		destroyMomentObserver() {
			if (this.momentObserver) {
				this.momentObserver.disconnect()
				this.momentObserver = null
			}
		},
		like(id) {
			if (this.isLike(id)) {
				this.$notify({title: '不可重复点赞', type: 'warning'})
				return
			}
			likeMoment(id).then(res => {
				if (res.code !== 200) {
					this.$notify({title: res.msg, type: 'warning'})
					return
				}
				this.$notify({title: res.msg, type: 'success'})
				this.likeMomentIds.push(id)
				this.momentList.forEach(item => {
					if (item.id === id) {
						item.likes++
					}
				})
			}).catch(() => {
				this.$notify({title: '异常错误', type: 'error'})
			})
		}
	}
}
</script>

<style scoped>
.moments-page {
	padding-bottom: 8px;
}

.page-header {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	margin-bottom: 18px;
	padding: 18px 20px;
	border-radius: 22px;
	background:
		radial-gradient(circle at top left, rgba(96, 165, 250, .20), transparent 42%),
		linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%);
	border: 1px solid #e1ebf5;
	box-shadow: 0 14px 30px rgba(15, 23, 42, .05);
}

.page-header h2 {
	margin: 0;
	font-size: 30px;
	line-height: 1.1;
	color: #0f172a;
}

.page-header p {
	margin: 8px 0 0;
	color: #64748b;
	font-size: 14px;
	line-height: 1.7;
}

.date-filter {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	padding: 6px 10px;
	border-radius: 999px;
	font-size: 12px;
	color: #334155;
}

.date-filter button {
	border: 0;
	padding: 2px 8px;
	border-radius: 999px;
	background: #1d4ed8;
	color: #fff;
	cursor: pointer;
}

.moments-layout {
	display: flex;
	gap: 20px;
	align-items: flex-start;
}

.moments-main {
	flex: 1;
	min-width: 0;
}

.pagination {
	margin-top: 12px;
	text-align: center;
}

@media (max-width: 1200px) {
	.page-header h2 {
		font-size: 26px;
	}
}

@media (max-width: 992px) {
	.page-header {
		display: block;
		padding: 16px;
		border-radius: 18px;
	}

	.date-filter {
		margin-top: 8px;
	}

	.moments-layout {
		display: block;
	}
}
</style>
